import classNames from 'classnames';
import CurrencyFormat from 'components/CurrencyFormat';
import type { DropdownOption } from 'components/DropdownListWithOptions';
import DurationFormat from 'components/DurationFormat';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import { Pin } from 'components/Icons';
import Label from 'components/Label';
import ProgressBar from 'components/ProgressBar';
import BudgetIsPendingWrap from 'components/projects/BudgetIsPendingWrapper';
import ProjectBadge from 'components/projects/ProjectBadge';
import Truncate from 'components/Truncate';
import React, { useCallback } from 'react';
import type { UserPermissionsFacade } from 'redux/modules/user.types';
import type { Currency, Features } from 'types/account';
import type { EnrichedProjectWithBudget } from 'types/project';
import { getProjectColor } from 'utils/project';
import useProjectOptionsDropdown from '../../../routes/projects/hooks/useProjectOptionsDropdown';
import { Card } from './Card';
import styles from './ProjectCard.module.scss';

export interface ProjectCardProps {
  className?: string;
  currency: Currency;
  project: EnrichedProjectWithBudget;
  onClick: (project: EnrichedProjectWithBudget) => void;
  onOptionClick?: (option: DropdownOption, project: EnrichedProjectWithBudget) => void;
  permissions: UserPermissionsFacade;
  features: Features;
}
const ProjectCard = React.memo<ProjectCardProps>(props => {
  const { className, currency, permissions, project, features, onOptionClick, ...rest } = props;
  const { budgetAttributes } = project;

  // Convert dropdown items to legacy format for Card component
  const handleProjectActionRequest = useCallback(
    (action: 'delete' | 'archive', id: number, name: string) => {
      if (onOptionClick) {
        onOptionClick(
          {
            idRef: id,
            action,
            meta: name,
          } as DropdownOption,
          project
        );
      }
    },
    [onOptionClick, project]
  );

  // Use our hook to get the dropdown items
  const { dropdownItems } = useProjectOptionsDropdown({
    projectId: project.base.id,
    projectName: project.base.name,
    isPinned: project.isPinned,
    isActive: project.base.active,
    isProjectsDashboardFeatureLocked: !features.project_dashboard,
    permissions,
    features,
    onProjectActionRequest: handleProjectActionRequest,
    tic: project.base.tic,
  });

  // No need to convert dropdown items anymore, as Card now accepts them directly

  const containerClass = classNames(styles.projectCard, className);
  const isPending = budgetAttributes.budgetIsPending;

  const labelClass = classNames(styles.label, {
    [styles.overLimit]: budgetAttributes.budgetSpendInPercent > 100,
  });

  return (
    <Card
      {...rest}
      className={containerClass}
      clickReference={project}
      dropdownItems={dropdownItems}
      optionsIconClassName={project.isPinned ? styles.optionsShowOnHover : undefined}
    >
      {project.isPinned && (
        <div className={styles.pinHideOnHover}>
          <Pin />
        </div>
      )}
      <div className={styles.cardBlocks} data-testid="project_card">
        <div className={styles.heading}>
          <ProjectBadge
            className={styles.badge}
            size="large"
            icon
            color={`#${getProjectColor(project.base)}`}
            ticMeta={project.base.tic}
          />
          <h3 className="fs-mask" data-testid="project_name_project_card">
            <Truncate lines={5}>
              <DiagnosticResourceId resource="project" id={project.base.id} />
              {project.base.name}
            </Truncate>
          </h3>
          <small>
            <span className="fs-mask" data-testid="project_client_project_card">
              <DiagnosticResourceId resource="client" id={project.base.client.id} />
              {project.base.client.name}
            </span>
            {!project.base.active && (
              <Label className={styles.label} size="small">
                Archived
              </Label>
            )}
          </small>
        </div>
        <div className={styles.footer}>
          {budgetAttributes.hasBudget && (
            <div className={styles.budget}>
              <ProgressBar
                size="small"
                value={!isPending ? budgetAttributes.budgetSpendInPercent : undefined}
                secondaryValue={isPending ? 100 : undefined}
                secondaryProgressColor="#b4b9bf"
                data-testid="budget-progress"
              />
              <div className={labelClass}>
                <BudgetIsPendingWrap isPending={isPending}>
                  {budgetAttributes.budgetIsHours ? (
                    <DurationFormat
                      value={Math.abs(budgetAttributes.budgetRemaining)}
                      valueType="hours"
                    />
                  ) : (
                    <CurrencyFormat
                      value={Math.abs(budgetAttributes.budgetRemaining)}
                      currency={project.fullProject?.currency}
                    />
                  )}{' '}
                  {budgetAttributes.budgetSpendInPercent > 100 ? 'over' : 'left'}
                </BudgetIsPendingWrap>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
});

ProjectCard.displayName = 'ProjectCard';
export { ProjectCard };
